import React, { useState, useEffect } from 'react';
import { XMarkIcon, UserPlusIcon, PencilSquareIcon } from '@heroicons/react/24/outline';
import { Lead, LeadFormData, LeadStatus, LeadSource } from '../../types/lead';
import { leadService } from '../../services/leadService';
import { useAuth } from '../../contexts/AuthContext';
import toast from 'react-hot-toast';

interface LeadFormModalProps {
  lead?: Lead | null;
  onClose: () => void;
  onSuccess: () => void;
}

const LeadFormModal: React.FC<LeadFormModalProps> = ({ lead, onClose, onSuccess }) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState<LeadFormData>({
    name: '',
    email: '',
    phone: '',
    company: '',
    status: 'new',
    source: 'manual',
    score: undefined,
    notes: '',
    nextFollowUpAt: undefined,
    assignedTo: '',
  });

  useEffect(() => {
    if (lead) {
      setFormData({
        name: lead.name,
        email: lead.email || '',
        phone: lead.phone || '',
        company: lead.company || '',
        status: lead.status,
        source: lead.source,
        score: lead.score,
        notes: lead.notes || '',
        nextFollowUpAt: lead.nextFollowUpAt,
        assignedTo: lead.assignedTo || '',
      });
    }
  }, [lead]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!user?.organizationId) {
      toast.error('Nincs szervezet kiválasztva');
      return;
    }

    try {
      setLoading(true);
      
      // Távolítsuk el az undefined mezőket
      const cleanedData: any = { ...formData };
      Object.keys(cleanedData).forEach(key => {
        if (cleanedData[key] === undefined || cleanedData[key] === '') {
          delete cleanedData[key];
        }
      });
      
      if (lead) {
        await leadService.updateLead(lead.id, cleanedData, user.id);
        toast.success('Lead sikeresen frissítve');
      } else {
        await leadService.createLead(cleanedData, user.organizationId, user.id, user.displayName || user.email);
        toast.success('Lead sikeresen létrehozva');
      }
      
      onSuccess();
    } catch (error) {
      console.error('Error saving lead:', error);
      toast.error('Hiba a lead mentésekor');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity bg-black bg-opacity-50" onClick={onClose} />

        <div className="inline-block w-full max-w-4xl my-8 overflow-hidden text-left align-middle transition-all transform bg-white dark:bg-gray-800 shadow-2xl rounded-xl">
          {/* Modern Header with Gradient */}
          <div className="bg-gradient-to-r from-primary-600 via-blue-600 to-cyan-600 p-6 rounded-t-xl">
            <div className="flex items-start justify-between">
              <div className="flex items-start space-x-4">
                <div className="p-3 bg-white/20 rounded-lg backdrop-blur-sm">
                  {lead ? (
                    <PencilSquareIcon className="h-7 w-7 text-white" />
                  ) : (
                    <UserPlusIcon className="h-7 w-7 text-white" />
                  )}
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-white mb-1">
                    {lead ? 'Lead szerkesztése' : 'Új Lead létrehozása'}
                  </h3>
                  <p className="text-sm text-white/80">
                    {lead ? 'Módosítsd a lead adatait' : 'Adj hozzá egy új lead-et a rendszerhez'}
                  </p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="p-2 text-white/80 hover:text-white hover:bg-white/20 rounded-lg transition-all"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="px-6 py-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Név */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Név <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                />
              </div>

              {/* Email */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                />
              </div>

              {/* Telefon */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Telefon
                </label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                />
              </div>

              {/* Cég */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Cég
                </label>
                <input
                  type="text"
                  value={formData.company}
                  onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                />
              </div>

              {/* Státusz */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Státusz
                </label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value as LeadStatus })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                >
                  <option value="new">Új</option>
                  <option value="contacted">Kapcsolatba lépve</option>
                  <option value="qualified">Minősített</option>
                  <option value="unqualified">Nem minősített</option>
                </select>
              </div>

              {/* Forrás */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Forrás
                </label>
                <select
                  value={formData.source}
                  onChange={(e) => setFormData({ ...formData, source: e.target.value as LeadSource })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                >
                  <option value="manual">Manuális</option>
                  <option value="google_sheets">Google Sheets</option>
                  <option value="website">Weboldal</option>
                  <option value="referral">Ajánlás</option>
                  <option value="other">Egyéb</option>
                </select>
              </div>

              {/* Score */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Pontszám (0-100)
                </label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={formData.score || ''}
                  onChange={(e) => setFormData({ ...formData, score: e.target.value ? parseInt(e.target.value) : undefined })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                />
              </div>

              {/* Megjegyzések */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Megjegyzések
                </label>
                <textarea
                  rows={4}
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                />
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center justify-end space-x-3 mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
              >
                Mégse
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors disabled:opacity-50"
              >
                {loading ? 'Mentés...' : lead ? 'Frissítés' : 'Létrehozás'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default LeadFormModal;
